import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import { StatusActionButton, StatsGrid, StatItem, ActionsMenuBar, ActionItem } from '../../../shared/ui';
import { estimatesApi, Estimate } from '../../../api/estimatesApi';
import EstimateDetailModal from './EstimateDetailModal';
import { useToast } from '../../../shared/contexts/ToastContext';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { sitesV2Api } from '../../../api/sitesV2Api';
import { siteMemberPermissionsApi } from '../../../api/siteMemberPermissionsApi';
import './EstimatesCard.css';

interface EstimatesCardProps {
  siteId: number;
}

// Компонент для элемента сметы с поддержкой свайпа
const SwipeableEstimateItem: React.FC<{
  estimate: Estimate;
  isSelected: boolean;
  onSelect: () => void;
  onClick: (wasSwiped: boolean) => void;
  formatDate: (dateString: string) => string;
}> = ({ estimate, isSelected, onSelect, onClick, formatDate }) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [wasSwiped, setWasSwiped] = useState(false);
  const swipeStartX = useRef(0);
  const swipeStartY = useRef(0);
  const itemRef = useRef<HTMLDivElement>(null);

  const handleSwipeStart = useCallback((clientX: number, clientY: number) => {
    swipeStartX.current = clientX;
    swipeStartY.current = clientY;
    setIsSwiping(true);
  }, []);

  const handleSwipeMove = useCallback((clientX: number, clientY: number) => {
    if (!isSwiping) return;

    const deltaX = clientX - swipeStartX.current;
    const deltaY = Math.abs(clientY - swipeStartY.current);

    // Если вертикальный свайп больше горизонтального, отменяем
    if (deltaY > 10 && Math.abs(deltaX) < 10) {
      setIsSwiping(false);
      return;
    }

    if (deltaX < 0) {
      const offset = Math.max(deltaX, -80);
      setSwipeOffset(offset);
    } else if (swipeOffset < 0) {
      const offset = Math.min(deltaX + swipeOffset, 0);
      setSwipeOffset(offset);
    }
  }, [isSwiping, swipeOffset]);

  const handleSwipeEnd = useCallback(() => {
    setIsSwiping(false);

    // Если свайпнули достаточно далеко, выделяем/снимаем выделение
    if (swipeOffset < -60) {
      setWasSwiped(true);
      
      if (itemRef.current) {
        itemRef.current.style.transition = 'none';
        itemRef.current.style.transform = 'translateX(0px)';
        void itemRef.current.offsetHeight;
      }
      
      setSwipeOffset(0);
      
      setTimeout(() => {
        onSelect();
        setTimeout(() => {
          if (itemRef.current) {
            itemRef.current.style.transition = '';
          }
          setTimeout(() => {
            setWasSwiped(false);
          }, 100);
        }, 10);
      }, 0);
    } else {
      setSwipeOffset(0);
      setWasSwiped(false);
    }
  }, [swipeOffset, onSelect]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) return;
    handleSwipeStart(e.touches[0].clientX, e.touches[0].clientY);
  }, [handleSwipeStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (isSwiping) {
      handleSwipeMove(e.touches[0].clientX, e.touches[0].clientY);
    }
  }, [isSwiping, handleSwipeMove]);

  const handleTouchEnd = useCallback(() => {
    if (isSwiping) {
      handleSwipeEnd();
    }
  }, [isSwiping, handleSwipeEnd]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) return;
    handleSwipeStart(e.clientX, e.clientY);
  }, [handleSwipeStart]);

  useEffect(() => {
    if (!isSwiping) return;

    const handleMouseMove = (e: MouseEvent) => {
      handleSwipeMove(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      handleSwipeEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isSwiping, handleSwipeMove, handleSwipeEnd]);

  const handleClick = () => {
    onClick(wasSwiped);
  };

  return (
    <div className="estimate-item-wrapper">
      {/* Иконка выделения под карточкой */}
      <div 
        className="estimate-item__swipe-action"
        style={{
          opacity: Math.min(Math.abs(swipeOffset) / 60, 1)
        }}
      >
        <i className={isSelected ? "ri-checkbox-circle-fill" : "ri-checkbox-circle-line"}></i>
      </div>
      
      {/* Сама карточка с свайпом */}
      <div
        ref={itemRef}
        className={`estimate-item ${estimate.isClosed ? 'closed' : ''} ${isSelected ? 'estimate-item--selected' : ''}`}
        style={{
          transform: `translateX(${swipeOffset}px)`,
          transition: isSwiping ? 'none' : undefined
        }}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        <div className="estimate-header">
          <h3 className="estimate-name">{estimate.name}</h3>
          <StatusActionButton
            variant={estimate.isClosed ? 'danger' : 'success'}
            size="small"
          >
            {estimate.isClosed ? 'Закрыта' : 'Активна'}
          </StatusActionButton>
        </div>
        {estimate.description && (
          <p className="estimate-description">{estimate.description}</p>
        )}
        <div className="estimate-footer">
          <span className="estimate-date">
            Создана: {formatDate(estimate.dateCreated)}
          </span>
          {estimate.dateUpdated && (
            <span className="estimate-date">
              Обновлена: {formatDate(estimate.dateUpdated)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const EstimatesCard: React.FC<EstimatesCardProps> = ({ siteId }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [statistics, setStatistics] = useState<{ 
    total: number; 
    active: number; 
    closed: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedEstimateId, setSelectedEstimateId] = useState<number | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newEstimate, setNewEstimate] = useState({
    name: '',
    description: ''
  });
  const [selectedEstimates, setSelectedEstimates] = useState<Set<number>>(new Set());
  const [showActionsMenuBar, setShowActionsMenuBar] = useState(false);
  const [hasFinanceViewPermission, setHasFinanceViewPermission] = useState<boolean>(false);
  const [hasFinanceEditPermission, setHasFinanceEditPermission] = useState<boolean>(false);
  const { showError, showSuccess } = useToast();
  const { user } = useAuth();

  // Проверка прав на работу с финансами
  const checkFinancePermission = useCallback(async () => {
    try {
      const members = await sitesV2Api.getSiteMembers(siteId);
      const currentUserMember = members.find(m => m.userId === user?.id);
      
      if (currentUserMember) {
        const perms = await siteMemberPermissionsApi.getMemberPermissions(siteId, currentUserMember.id);
        const hasViewPermission = perms.some(p => p.permissionCode === 'FINANCE_VIEW_DATA');
        const hasEditPermission = perms.some(p => p.permissionCode === 'FINANCE_EDIT');
        setHasFinanceViewPermission(hasViewPermission);
        setHasFinanceEditPermission(hasEditPermission);
      } else {
        setHasFinanceViewPermission(false);
        setHasFinanceEditPermission(false);
      }
    } catch (err) {
      console.error('Error checking finance permission:', err);
      setHasFinanceViewPermission(false);
      setHasFinanceEditPermission(false);
    }
  }, [siteId, user?.id]);

  // Загрузка данных
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Проверяем права на работу с финансами
      await checkFinancePermission();
      
      try {
        const estimatesData = await estimatesApi.getSiteEstimates(siteId);
        setEstimates(estimatesData);
        
        // Вычисляем статистику
        const stats = {
          total: estimatesData.length,
          active: estimatesData.filter(e => !e.isClosed).length,
          closed: estimatesData.filter(e => e.isClosed).length
        };
        setStatistics(stats);
      } catch (err: any) {
        // Если получили 403, значит нет прав
        if (err?.response?.status === 403) {
          setHasFinanceViewPermission(false);
          setHasFinanceEditPermission(false);
          setEstimates([]);
          setStatistics(null);
        } else {
          console.error('Error loading estimates:', err);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, checkFinancePermission]);

  // Ленивая загрузка: загружаем данные когда карточка раскрыта
  useEffect(() => {
    if (!isCollapsed || isFullscreenOpen) {
      loadData();
    }
  }, [isCollapsed, isFullscreenOpen, loadData]);

  // Подготовка данных для StatsGrid
  const statsData: StatItem[] = [
    {
      id: 'total',
      value: statistics?.total || 0,
      label: 'Всего',
      icon: 'ri-file-list-3-line',
      color: 'muted'
    },
    {
      id: 'active',
      value: statistics?.active || 0,
      label: 'Активных',
      icon: 'ri-checkbox-circle-line',
      color: 'success'
    },
    {
      id: 'closed',
      value: statistics?.closed || 0,
      label: 'Закрытых',
      icon: 'ri-archive-line',
      color: 'error'
    }
  ];

  // Форматирование даты (используется в основном компоненте и в SwipeableEstimateItem)
  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  // Обработчик выделения сметы
  const handleEstimateSelect = useCallback((estimate: Estimate) => {
    setSelectedEstimates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(estimate.id)) {
        newSet.delete(estimate.id);
      } else {
        newSet.add(estimate.id);
      }
      
      // Показываем меню если есть выделенные сметы
      if (newSet.size > 0) {
        setShowActionsMenuBar(true);
      } else {
        setShowActionsMenuBar(false);
      }
      
      return newSet;
    });
  }, []);

  // Эффект для показа меню при изменении выделенных смет
  useEffect(() => {
    if (selectedEstimates.size > 0) {
      setShowActionsMenuBar(true);
    } else {
      setShowActionsMenuBar(false);
    }
  }, [selectedEstimates]);

  // Обработчик клика на смету (только если не был свайп)
  const handleEstimateClick = (estimate: Estimate, wasSwiped: boolean = false) => {
    if (wasSwiped || selectedEstimates.size > 0) {
      return;
    }
    setSelectedEstimateId(estimate.id);
    setIsDetailModalOpen(true);
  };

  // Обработчик закрытия модального окна
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedEstimateId(null);
  };

  // Создание новой сметы
  const handleCreateEstimate = async () => {
    if (!newEstimate.name.trim()) {
      alert('Введите название сметы');
      return;
    }

    try {
      const createdEstimate = await estimatesApi.createEstimate(siteId, {
        name: newEstimate.name.trim(),
        description: newEstimate.description.trim() || ''
      });
      
      // Обновляем список смет
      await loadData();
      
      // Закрываем форму и открываем созданную смету
      setShowCreateModal(false);
      setNewEstimate({ name: '', description: '' });
      setSelectedEstimateId(createdEstimate.id);
      setIsDetailModalOpen(true);
    } catch (err) {
      console.error('Error creating estimate:', err);
      alert('Не удалось создать смету');
    }
  };

  // Удаление выделенных смет
  const handleDelete = useCallback(async () => {
    if (selectedEstimates.size === 0) {
      return;
    }

    const estimatesToDelete = estimates.filter(e => selectedEstimates.has(e.id));
    const count = estimatesToDelete.length;
    
    const confirmed = window.confirm(
      count === 1
        ? `Вы уверены, что хотите удалить смету "${estimatesToDelete[0].name}"?`
        : `Вы уверены, что хотите удалить ${count} смет?`
    );

    if (!confirmed) {
      return;
    }

    try {
      await Promise.all(
        estimatesToDelete.map(estimate => estimatesApi.deleteEstimate(siteId, estimate.id))
      );
      showSuccess(count === 1 ? 'Смета успешно удалена' : `${count} смет успешно удалено`);
      setSelectedEstimates(new Set());
      setShowActionsMenuBar(false);
      await loadData();
    } catch (err: any) {
      console.error('Error deleting estimates:', err);
      showError(err.response?.data?.message || 'Ошибка удаления смет');
    }
  }, [selectedEstimates, estimates, siteId, showSuccess, showError, loadData]);

  // Закрытие меню действий
  const handleCloseActionsMenuBar = useCallback(() => {
    setShowActionsMenuBar(false);
    setSelectedEstimates(new Set());
  }, []);

  // Отмена выделения
  const handleCancelSelection = useCallback(() => {
    setSelectedEstimates(new Set());
  }, []);

  // Подготовка действий для меню
  const estimateActions: ActionItem[] = React.useMemo(() => {
    if (selectedEstimates.size === 0) return [];

    const isSingle = selectedEstimates.size === 1;

    return [
      {
        id: 'delete',
        label: isSingle ? 'Удалить' : `Удалить (${selectedEstimates.size})`,
        icon: 'ri-delete-bin-line',
        onClick: handleDelete,
        variant: 'danger'
      }
    ];
  }, [selectedEstimates, handleDelete]);

  return (
    <DashboardCard
      id="estimates-card"
      title="Сметы"
      icon="ri-file-list-3-line"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      onFullscreenOpenChange={setIsFullscreenOpen}
      expandMode="fullscreen"
      className="estimates-card"
    >
      {!hasFinanceViewPermission ? (
        <div className="estimates-no-permission">
          <i className="ri-lock-line"></i>
          <p>Нет прав для просмотра данной информации</p>
        </div>
      ) : (
        <>
          {/* Кнопка создания сметы - показываем только если есть право на редактирование */}
          {hasFinanceEditPermission && (
            <div className="estimates-actions">
              <StatusActionButton
                variant="primary"
                fullWidth
                onClick={() => setShowCreateModal(true)}
                disabled={loading}
              >
                Создать смету
              </StatusActionButton>
            </div>
          )}

          {statistics && (
            <StatsGrid items={statsData} />
          )}

          {/* Модальное окно создания сметы */}
          {showCreateModal && (
        <div className="create-estimate-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="create-estimate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="create-estimate-header">
              <h3>Создать новую смету</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewEstimate({ name: '', description: '' });
                }}
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="create-estimate-body">
              <div className="form-group">
                <label>Название сметы *</label>
                <input
                  type="text"
                  placeholder="Например: Смета на ремонт квартиры"
                  value={newEstimate.name}
                  onChange={(e) => setNewEstimate({ ...newEstimate, name: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleCreateEstimate()}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Описание</label>
                <textarea
                  placeholder="Дополнительная информация о смете (необязательно)"
                  value={newEstimate.description}
                  onChange={(e) => setNewEstimate({ ...newEstimate, description: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
            <div className="create-estimate-footer">
              <button 
                className="btn-cancel"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewEstimate({ name: '', description: '' });
                }}
              >
                Отмена
              </button>
              <button 
                className="btn-create"
                onClick={handleCreateEstimate}
                disabled={!newEstimate.name.trim()}
              >
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

          {estimates.length === 0 ? (
            <div className="estimates-empty">
              <p>Сметы не найдены</p>
            </div>
          ) : (
            <div className="estimates-list">
              {estimates.map((estimate) => (
                <SwipeableEstimateItem
                  key={estimate.id}
                  estimate={estimate}
                  isSelected={selectedEstimates.has(estimate.id)}
                  onSelect={() => handleEstimateSelect(estimate)}
                  onClick={(wasSwiped) => handleEstimateClick(estimate, wasSwiped)}
                  formatDate={formatDate}
                />
              ))}
            </div>
          )}

          {/* Модальное окно с деталями сметы */}
          {selectedEstimateId && (
            <EstimateDetailModal
              isOpen={isDetailModalOpen}
              onClose={handleCloseDetailModal}
              siteId={siteId}
              estimateId={selectedEstimateId}
            />
          )}

          {/* Меню действий со сметами */}
          <ActionsMenuBar
            isOpen={showActionsMenuBar}
            actions={estimateActions}
            onClose={handleCloseActionsMenuBar}
            onCancel={handleCancelSelection}
          />
        </>
      )}
    </DashboardCard>
  );
};

export default EstimatesCard;

